import { useAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";

import { GradientButton } from "../shared/components/fkl-gradient-button/fkl-gradient-button.component";
import { QuizProgressBar } from "../shared/components/lander-quiz-components/quiz-progress-bar/quiz-progress-bar.component";
import { MediaDisplayer } from "../shared/components/lander-quiz-media-displayer/media-displayer/media-displayer.component";
import {
  videoLimitAtom,
  videoLimitFinishedAtom,
} from "../shared/components/lander-quiz-media-displayer/media-displayer/media-displayer.state";
import { ProductDetailsModal } from "../shared/components/product-modal/product-modal.component";
import { getTotalSweepEntries, pluralizeEntries } from "../shared/utils";

import { EcommerceProduct } from "./ecommerce-video.types";

export function Widget() {
  const collection = window.FKL.ecommerceProductCollection;
  const products = window.FKL.ecommerceProductCollection.products;
  const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
  const email: Optional<string> = submission?.data?.email;

  const [videoLimit] = useAtom(videoLimitAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoLimitFinished] = useAtom(videoLimitFinishedAtom);
  const [message, setMessage] = useState<string | null>(null);
  const [currentProduct, setCurrentProduct] = useState<number>(0);

  const toggleModalDisplay = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const skipEcommerceSection = () => {
    window.theme.EcommerceVideoUtils.skip();
  };

  const totalEntries = useMemo(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return getTotalSweepEntries(submission?.data) || 0;
  }, []);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>, product: EcommerceProduct) => {
    if (!videoLimitFinished) {
      event.preventDefault();
      setMessage(
        `Please watch ${videoLimit} seconds of the video before answering.`
      );
    } else {
      window.snaptr("track", "ADD_CART", {
        price: product.price,
        currency: "USD",
        item_ids: [product.id],
        item_category: "product",
        number_items: 1,
        description: product.title,
        user_email: email,
      });

      window.ttq.track("AddToCart", {
        contents: [
          {
            content_id: product.id,
            content_type: "product",
            content_name: product.title,
          }
        ],
        value: product.price,
        currency: "USD"
      });

      window.twq(
        "event",
        `tw-${window.FKL.x_pixel_id}-${window.FKL.x_add_to_cart_event_id}`,
        {
          value: product.price,
          currency: "USD",
          contents: [
            {
              content_type: "product",
              content_id: product.id,
              content_name: product.title,
              content_price: product.price,
              num_items: 1,
              content_group_id: null,
            },
          ],
          email_address: email,
        }
      );
      setMessage(null);
    }
  };

  const nextProduct = () => {
    if (!videoLimitFinished) {
      setMessage(
        `Please watch ${videoLimit} seconds of the video before answering.`
      );
    } else {
      setMessage(null);
      if (currentProduct + 1 < collection.products.length) {
        setCurrentProduct(currentProduct + 1);
      }
    }
  };

  const isLastProduct = currentProduct === products.length - 1;

  return (
    <>
      <div className="lander__cpl__quiz__header">
        <div className="lander__cpl__quiz__header--earned-entries">
          {`Your entries: ${totalEntries}`}
        </div>
        <span className="lander__cpl__quiz__header--description">
          Shop FindKeepLove to earn extra entries
        </span>
        <QuizProgressBar
          branchedQuestionsCount={0}
          quizQuestionsCount={products.length}
          questionsAnsweredCount={currentProduct + 1}
        />
      </div>
      <div className="question__body">
        <div className="question__media">
          <MediaDisplayer
            media={products[currentProduct].media}
            stepIndex={currentProduct}
          />
        </div>
        <div className="product-information">
          <img
            className="lander__cpl__quiz--media-logo"
            src={products[currentProduct].logo}
            alt="Brand Logo"
          />
          <button className="modal__link" onClick={toggleModalDisplay}>
            {" "}
            View Product Details{" "}
          </button>
        </div>
        <div className="lander__cpl__quiz__body">
          <div className="lander__cpl__quiz__body--question-container">
            <div className="lander__cpl__quiz__body--question-header">
              <div className="lander__cpl__quiz__body--question-entries">
                {`+${products[currentProduct].entries}`} <br />{" "}
                {`${pluralizeEntries(products[currentProduct].entries)}`}
              </div>
              <div className="lander__cpl__quiz__body--question-title">
                {products[currentProduct].question}
              </div>
            </div>
            <div className="lander__cpl__quiz__body--question-actions">
              <form
                onSubmit={event => handleFormSubmit(event, products[currentProduct])}
                method="post"
                action="/cart/add"
                data-testid="cart-form"
              >
                <input type="hidden" name="return_to" value="/checkout" />
                <input
                  type="hidden"
                  name="id"
                  value={products[currentProduct].id}
                />
                <div className="gradient-border">
                  <input
                    type="submit"
                    value="Yes, Add to cart"
                    name="checkout"
                    className="lander-quiz--button"
                  />
                </div>
              </form>
              {!isLastProduct && products.length > 1 && (
                <GradientButton
                  grayBackground={!videoLimitFinished}
                  onClick={nextProduct}
                >
                  Next
                </GradientButton>
              )}
            </div>
            <div className="page--indicator">
              {" "}
              ({currentProduct + 1}/{products.length})
            </div>
            {message && !videoLimitFinished && (
              <span
                className="lander__cpl__quiz__footer--message red-alert--message"
                data-testid="ecommerce--warning"
              >
                {message}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="lander__quiz__footer">
        <button
          className="lander__quiz__footer--skip-btn"
          onClick={skipEcommerceSection}
        >
          No thanks, continue without purchasing
        </button>
        <span className="sweepstake-ecommerce__disclaimer">
          {collection.disclaimer}
        </span>
      </div>
      <ProductDetailsModal
        isOpen={isModalOpen}
        onClose={toggleModalDisplay}
        content={products[currentProduct].description}
      />
    </>
  );
}
